import { sample } from 'effector';
import notification from 'antd/es/notification';
import { combineEvents } from 'patronum';
import {
  $analyticsData,
  $analyticsDistricts,
  $analyticsFilters,
  $analyticsKrp,
  $analyticsLastDate,
  $analyticsRegions,
  $filters,
  $orgsData,
  $orgsTable,
  $reportsList,
  $reportsModal,
  $selectedOkedData,
  $selectedRegionData,
  $tableControl,
} from './stores.js';
import {
  downloadReportFx,
  exportToExcelFx,
  getAnalyticsCompanyCountFx,
  getAnalyticsCountFx,
  getAnalyticsDataFx,
  getAnalyticsKrpFx,
  getAnalyticsLastDateFx,
  getAnalyticsOkedFx,
  getAnalyticsRegionFx,
  getAnalyticsRegionsFx,
  getAnalyticsTaxCountFx,
  getAnalyticsTaxFx,
  getOrganizationsDataFx,
  getOrganizationsTableFx,
  getReportsListFx,
} from './effects.js';
import {
  AnalyticsGate,
  applyAnalyticsFiltersEv,
  changeFiltersEv,
  changeSelectedOkedDataEv,
  changeSelectedRegionDataEv,
  changeTableStateEv,
  downloadReportEv,
  exportExcelEv,
  getReportsListEv,
  openReportsModalEv,
  OrgsDashboardGate,
  ReportsModalGate,
  resetAnalyticsFiltersEv,
  resetFiltersEv,
  resetReportsModalEv,
  sendAnalyticsCallEv,
  sendOrgsCallEv,
} from './events.js';

const getAnalyticsDataEv = combineEvents({
  events: {
    // companyCount: getAnalyticsCompanyCountFx.doneData,
    // taxCount: getAnalyticsTaxCountFx.doneData,
    oked: getAnalyticsOkedFx.doneData,
    region: getAnalyticsRegionFx.doneData,
    tax: getAnalyticsTaxFx.doneData,
    count: getAnalyticsCountFx.doneData,
  },
});

$orgsData
  .on(getOrganizationsDataFx.doneData, (state, payload) => payload)
  .reset(OrgsDashboardGate.close);

$orgsTable
  .on(getOrganizationsTableFx.doneData, (state, payload) => payload)
  .reset(OrgsDashboardGate.close);

$filters
  .on(changeFiltersEv, (state, payload) => {
    if (Array.isArray(payload)) {
      const newState = Object.fromEntries(
        payload.flatMap((obj) => Object.entries(obj))
      );
      return { ...state, ...newState };
    }
    return { ...state, ...payload };
  })
  .reset([resetFiltersEv, OrgsDashboardGate.close]);

$tableControl
  .on(changeTableStateEv, (state, payload) => {
    return { ...state, ...payload };
  })
  .reset([OrgsDashboardGate.close, $filters.updates]);

$reportsModal
  .on(openReportsModalEv, (state, payload) => {
    if (typeof payload === 'string') {
      return payload;
    }
    return true;
  })
  .reset(resetReportsModalEv);

$reportsList
  .on(getReportsListFx.doneData, (state, payload) =>
    payload.toSorted(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
  )
  .reset(resetReportsModalEv);

$analyticsFilters
  .on(applyAnalyticsFiltersEv, (state, payload) => {
    return {
      ...state,
      ...payload,
    };
  })
  .reset(resetAnalyticsFiltersEv);

$analyticsData.on(getAnalyticsDataEv, (state, payload) => {
  return {
    company_count_all: +payload.count.company_count_all,
    tax_sum_all: payload.count.tax_sum_all,
    company_count_filtered: `${+payload.count.company_count_filtered}%`,
    tax_sum_filtered: `${payload.count.tax_sum_filtered}%`,
    ...payload,
  };
});

$selectedOkedData.reset(getAnalyticsDataEv);
$selectedRegionData.reset(getAnalyticsDataEv);

$analyticsRegions.on(getAnalyticsRegionsFx.doneData, (_, payload) => {
  const regions = Array.from(
    new Set(payload.map(({ region }) => region))
  ).filter((item) => item);
  return regions.map((region) => {
    return {
      value: region,
      label: region.charAt(0).toUpperCase() + region.slice(1).toLowerCase(),
    };
  });
});

$analyticsDistricts.on(getAnalyticsRegionsFx.doneData, (_, payload) => {
  const districts = Array.from(
    new Set(payload.map(({ district }) => district))
  ).filter((item) => item);
  return districts.map((district) => {
    return {
      value: district,
      label: district.charAt(1).toUpperCase() + district.slice(2).toLowerCase(),
    };
  });
});
$analyticsKrp.on(getAnalyticsKrpFx.doneData, (_, payload) => {
  return payload.map(({ krp_name_ru }) => {
    return {
      label: krp_name_ru,
      value: krp_name_ru,
    };
  });
});

$analyticsLastDate.on(getAnalyticsLastDateFx.doneData, (_, payload) => payload);

sample({
  clock: OrgsDashboardGate.open,
  target: [getOrganizationsDataFx, getOrganizationsTableFx],
});

const triggerKeys = [
  'show_all',
  'have_dvc',
  'is_gov',
  'have_contacts',
  'region',
  'district',
  'krp',
  'org_type',
  'legal_type',
  'department',
  'priority',
  'oked_level_0',
  'oked_level_1',
  'oked_level_2',
  'oked_level_3',
  'oked_level_4',
  'activity_category',
];
sample({
  source: $filters,
  clock: sendOrgsCallEv,
  filter: (filters) =>
    window.location.pathname.includes('organizations') &&
    Object.keys(filters).some((key) => triggerKeys.includes(key)),
  fn: (filters) => {
    const current = Object.fromEntries(
      Object.entries(filters).filter(
        ([key, value]) => triggerKeys.includes(key) && !!value
      )
    );
    return current;
  },
  target: getOrganizationsDataFx,
});

const triggerKeysTable = [...triggerKeys, 'bins'];
sample({
  source: { filters: $filters, tableState: $tableControl },
  clock: [sendOrgsCallEv, $tableControl.updates],
  filter: ({ filters, tableState }) =>
    window.location.pathname.includes('organizations') &&
    Object.keys(filters).some((key) => triggerKeysTable.includes(key)),
  fn: ({ filters, tableState }) => {
    const current = Object.fromEntries(
      Object.entries(filters).filter(
        ([key, value]) => triggerKeysTable.includes(key) && !!value
      )
    );
    return { ...current, ...tableState };
  },
  target: getOrganizationsTableFx,
});

sample({
  source: $filters,
  clock: exportExcelEv,
  fn: (filters) =>
    Object.fromEntries(
      Object.entries(filters).filter(([key, value]) => !!value)
    ),
  target: exportToExcelFx,
});

sample({
  clock: resetFiltersEv,
  target: [getOrganizationsDataFx, getOrganizationsTableFx],
});

sample({
  clock: downloadReportEv,
  target: downloadReportFx,
});

sample({
  clock: ReportsModalGate.state,
  filter: (gate) => Object.hasOwn(gate, 'type'),
  fn: (gate) => gate.type ?? undefined,
  target: getReportsListEv,
});

sample({
  clock: exportToExcelFx.doneData,
  fn: () => undefined,
  target: getReportsListEv,
});

sample({
  clock: getReportsListEv,
  target: getReportsListFx,
});

exportToExcelFx.done.watch(() =>
  notification.info({ message: 'Отчет принят в работу' })
);

sample({
  clock: AnalyticsGate.open,
  fn: () => {
    return {};
  },
  target: [getAnalyticsLastDateFx, getAnalyticsRegionsFx, getAnalyticsKrpFx],
});

sample({
  clock: $analyticsFilters.updates,
  fn: (filters) => {
    return {
      ...filters,
      region: filters.region ? filters.region : [],
      district: filters.district ? filters.district : [],
      activity_ids: filters.activity_ids ? filters.activity_ids : [],
      krp: filters.krp ? filters.krp : [],
      report_date: `${filters.year}-${
        filters.month.length < 2 ? `0${filters.month}` : filters.month
      }-01`,
    };
  },
  target: sendAnalyticsCallEv,
});

sample({
  clock: sendAnalyticsCallEv,
  target: [
    getAnalyticsOkedFx,
    getAnalyticsRegionFx,
    getAnalyticsTaxFx,
    // getAnalyticsCompanyCountFx,
    // getAnalyticsTaxCountFx,
    getAnalyticsCountFx,
  ],
});

sample({
  source: [$analyticsData, $selectedOkedData],
  clock: changeSelectedOkedDataEv,
  fn: ([zeroData, source], clock) => {
    if (source?.type && clock === source.type) {
      return null;
    }
    return {
      type: clock,
      data: zeroData.oked[clock],
    };
  },
  target: $selectedOkedData,
});

sample({
  source: [$analyticsData, $selectedRegionData],
  clock: changeSelectedRegionDataEv,
  fn: ([zeroData, source], clock) => {
    if (source?.type && clock === source.type) {
      return null;
    }
    return {
      type: clock,
      data: zeroData.region[clock],
    };
  },
  target: $selectedRegionData,
});

sample({
  clock: getAnalyticsLastDateFx.doneData,
  fn: (clock) => {
    return {
      month: clock.date.slice(5, 7),
      year: clock.date.slice(0, 4),
    };
  },
  target: applyAnalyticsFiltersEv,
});
